<template>
	<div class="pension-calculator-tab">
		<b-alert v-if="readOnly" show variant="primary">
			<strong>View only.</strong> You can see the pension calculator and its report but can't make changes.
		</b-alert>
		<pension-calculator
			:clientsProp="clientsSnapshot"
			:provinceProp="provinceSnapshot"
			:hireDateProp="null"
			:salaryProp="null"
			:cohabitationDateProp="cohabProp"
			:separationDateProp="sepProp"
			:relationshipStatusProp="relationshipStatus"
			:initialState="initialStateSnapshot"
			:read-only="readOnly"
			@update-state="onUpdateState"
			@select-result="onSelectResult"
		/>
	</div>
</template>

<script>
// Thin host wrapper around the lawyer-supplied pension calculator. It maps the
// matter's party/relationship data onto the calculator's prop contract (see the
// calculator's INTEGRATION.md) and persists the calculator's working state on
// the agreement so it survives a reload.
import debounce from 'lodash/debounce';
import { hasEditPermission } from '../../mixins/roleLevelPermissions';
import PensionCalculator from './PensionCalculator.vue';

// Full province/territory name (as stored on the matter address) → the
// jurisdiction codes the calculator understands.
const PROVINCE_CODES = {
	'british columbia': 'BC',
	alberta: 'AB',
	saskatchewan: 'SK',
	manitoba: 'MB',
	ontario: 'ON',
	quebec: 'QC',
	'new brunswick': 'NB',
	'nova scotia': 'NS',
	'prince edward island': 'PE',
	'newfoundland and labrador': 'NL',
	newfoundland: 'NL',
	yukon: 'YT',
	'northwest territories': 'NT',
	nunavut: 'NU',
};

function provinceCode(name) {
	if (!name) return 'BC';
	return PROVINCE_CODES[String(name).trim().toLowerCase()] || 'BC';
}

// The calculator only offers Female/Male; map from the matter's pronouns.
function toGender(pronouns) {
	const p = (pronouns || '').toLowerCase();
	if (p.startsWith('f') || p.includes('she')) return 'Female';
	if (p.startsWith('m') || p.includes('he')) return 'Male';
	return 'Female';
}

// Stored dates are ISO timestamps ("1981-02-02T08:00:00.000Z"); the calculator
// wants "yyyy-mm-dd". We must derive the LOCAL calendar date, not slice the UTC
// portion: the builder saves dates as a locally-parsed date -> toISOString(), and
// displays them back in local time. Slicing the UTC string shifts the day by one
// for timezones ahead of/behind UTC (e.g. IST turns 1995-01-01 into 1994-12-31).
function toIsoDate(value) {
	if (!value) return '';
	const s = String(value);
	// Already a plain calendar date (no time component) — pass through untouched.
	if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
	const d = new Date(s);
	if (isNaN(d.getTime())) return s.length >= 10 ? s.slice(0, 10) : s;
	const yyyy = d.getFullYear();
	const mm = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
}

function buildClient(partner, relationship) {
	if (!partner) return null;
	const name = partner.name
		? [partner.name.first, partner.name.last].filter(Boolean).join(' ').trim()
		: '';
	const rel = relationship || {};
	return {
		name,
		dob: toIsoDate(partner.birthdate),
		gender: toGender(partner.pronouns),
		cohabitationDate: toIsoDate(rel.cohabitationDate || rel.marriageDate || ''),
		separationDate: toIsoDate(rel.separationDate || ''),
	};
}

export default {
	name: 'PensionCalculatorTab',
	components: { PensionCalculator },
	props: {
		agreementData: { type: Object, required: true },
	},
	computed: {
		// Relationship dates stay live (reactive), not snapshotted, so the
		// calculator — where they are read-only — always shows the builder's
		// current values. The builder owns these dates.
		cohabProp() {
			const r = (this.agreementData && this.agreementData.relationship) || {};
			return toIsoDate(r.cohabitationDate || r.marriageDate || '');
		},
		sepProp() {
			const r = (this.agreementData && this.agreementData.relationship) || {};
			return toIsoDate(r.separationDate || '');
		},
		// Verbatim builder relationship status — the calculator uses it to flag the
		// "under two years with children" case where a valuation is not applicable.
		readOnly() {
			return !hasEditPermission('pension-calculator');
		},
		relationshipStatus() {
			const r = (this.agreementData && this.agreementData.relationship) || {};
			return r.status || '';
		},
	},
	data() {
		// Snapshot the inputs ONCE at creation. Persisting state re-fetches the
		// agreement (see updateAgreementFields), which would otherwise flow back
		// in as new props and reset the calculator mid-edit. The tab is rendered
		// behind a v-if, so it is recreated (and re-snapshotted from fresh matter
		// data) each time it is opened.
		const ad = this.agreementData || {};
		const rel = ad.relationship || {};
		const clients = [buildClient(ad.partner1, rel), buildClient(ad.partner2, rel)].filter(
			(c) => c && (c.name || c.dob)
		);
		const seedProvince =
			(ad.partner1 && ad.partner1.address && ad.partner1.address.province) ||
			(ad.partner2 && ad.partner2.address && ad.partner2.address.province) ||
			'';
		return {
			clientsSnapshot: clients,
			provinceSnapshot: provinceCode(seedProvince),
			initialStateSnapshot: ad.pensionCalculatorState || undefined,
			lastResult: null,
		};
	},
	created() {
		// Guard against redundant writes: the calculator emits update-state on
		// load with the restored state, and we only want to persist real changes.
		this.lastPersistedJson = JSON.stringify(this.agreementData.pensionCalculatorState || null);
		// The calculator emits on every keystroke; debounce before hitting Firestore.
		this.persist = debounce((state) => {
			if (this.readOnly) return;
			const json = JSON.stringify(state);
			if (json === this.lastPersistedJson) return;
			const id = this.$store.state.agreement && this.$store.state.agreement.id;
			if (!id) return;
			this.lastPersistedJson = json;
			this.$store.dispatch('updateAgreementFields', {
				id,
				fields: { pensionCalculatorState: state },
			});
		}, 800);
	},
	beforeDestroy() {
		if (this.persist) {
			this.persist.flush(); // write the final edit before the tab is torn down
			this.persist.cancel();
		}
	},
	methods: {
		onUpdateState(state) {
			this.persist(state);
		},
		onSelectResult(payload) {
			// Captured for a future phase (recording the chosen value into the
			// Property Schedule). The selection itself lives in update-state, so it
			// already persists; nothing to write here yet.
			this.lastResult = payload;
		},
	},
};
</script>

<style scoped>
.pension-calculator-tab {
	width: 100%;
}
</style>
